import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector, NgZone } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, tap, timeout } from 'rxjs/operators';
import { ErrorMessageService } from '../error-message.service';
import { AuthInterceptor } from './auth.interceptor.service';

@Injectable()
export class CentralizedErrorInterceptor implements HttpInterceptor {
  private toastr!: ToastrService;
  private ngZone!: NgZone;
  private errorMessageService!: ErrorMessageService;
  private authService!: AuthInterceptor;
  private logoutInProgress = false;

  private readonly toastConfig = {
    closeButton: true,
    progressBar: true,
    timeOut: 5000,
  };

  constructor(private injector: Injector) {}

  private initServices() {
    if (!this.toastr) {
      this.toastr = this.injector.get(ToastrService);
      this.ngZone = this.injector.get(NgZone);
      this.errorMessageService = this.injector.get(ErrorMessageService);
      this.authService = this.injector.get(AuthInterceptor);
    }
  }

  private showErrorToast(message: string, title: string) {
    this.ngZone.run(() => {
      this.toastr.error(message, title, this.toastConfig);
    });
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => this.handleError(error, req))
    );
  }

  private handleError(
    error: HttpErrorResponse,
    req: HttpRequest<any>
  ): Observable<never> {
    this.initServices();

    // Never block asset/i18n loads — those are used to translate other errors.
    if (this.isAssetRequest(req)) {
      return throwError(() => error);
    }

    if (error.status === 0) {
      this.showErrorToast(
        'Servers are down. Please contact support for assistance.',
        'Network Error'
      );
      this.authService.logout();
      return throwError(() => error);
    }

    if (error.status === 401 || error.status === 403) {
      if (!this.logoutInProgress) {
        this.logoutInProgress = true;
        const message =
          error.status === 401
            ? 'Your session has expired. Please sign in again.'
            : 'You are not authorized to perform this action.';

        this.showErrorToast(message, 'Unauthorized');
        this.authService.logout();
        setTimeout(() => (this.logoutInProgress = false), 1500);
      }

      return throwError(() => error);
    }

    const messageCode = this.extractMessageCode(error);
    const [messageKey, extraText] = this.splitMessageCode(messageCode);

    return from(this.errorMessageService.getErrorMessage(messageKey)).pipe(
      timeout(4000),
      catchError(() => of(messageKey)),
      tap((translatedMessage) => {
        const displayMessage = extraText
          ? `${translatedMessage} ${extraText}`
          : translatedMessage;
        this.showErrorToast(displayMessage, 'Error');
      }),
      switchMap(() => throwError(() => error))
    );
  }

  private extractMessageCode(error: HttpErrorResponse): string {
    const body = error.error;
    if (!body) {
      return error.status === 400 ? 'BAD REQUEST' : 'UNEXPECTED';
    }
    if (typeof body === 'string') {
      return body;
    }
    return (
      body.messageCode ||
      body.error ||
      body.message ||
      (error.status === 400 ? 'BAD REQUEST' : 'UNEXPECTED')
    );
  }

  private splitMessageCode(messageCode: string): [string, string] {
    if (!messageCode.includes(' - ')) {
      return [messageCode, ''];
    }
    const [messageKey, ...messageText] = messageCode.split(' - ');
    return [messageKey, messageText.join(' - ')];
  }

  private isAssetRequest(req: HttpRequest<any>): boolean {
    return req.url.includes('/assets/') || req.url.includes('assets/i18n');
  }
}
